import { PdfGen, PdfUnit, PdfSize, PdfPoint, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutSingleGen } from '../layoutSingle';
import { OptsSingle } from '../optsSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle, calcOrigin, fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/shapes';

/**
 * RollDiceFillBase (Hod kostkou a vybarvi)
 *
 * Creates a table where students roll dice and fill in exercises.
 * Each row corresponds to a dice sum, with exercises to solve.
 *
 * Migrated from C# EduFrame.Single.Generators.RollDiceFillBase
 */
abstract class RollDiceFillBase implements ILayoutSingleGen {
  constructor(
    protected readonly dices: number,
    protected readonly columns = 5
  ) {}

  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const totalSheets = 1;

    // Number of rows = max dice sum - min dice sum + 1
    const rows = this.dices * 6 - this.dices + 1;
    const { origin, size } = calcOrigin(this.columns + 1, rows, margin, PdfSize.A4);

    // Generate exercises
    const gens = genSingle(
      opts.data,
      opts.generator,
      this.columns * rows * totalSheets,
      false,
      false
    );

    for (let sIdx = 0; sIdx < totalSheets; sIdx++) {
      const page = pdf.addPage();
      const currentGens = gens.slice(sIdx * this.columns * rows, (sIdx + 1) * this.columns * rows);

      // Collect all texts to adjust font
      const allTexts = currentGens.map((g) => fixGenArray(g.texts));
      const font = page.adjustFontMulti(allTexts, GUtil.baseFont, size);

      for (let row = 0; row < rows; row++) {
        const number = row + this.dices; // Dice sum for this row

        for (let column = 0; column < this.columns + 1; column++) {
          const rect = new PdfRect(
            origin.x + column * size.width,
            origin.y + row * size.height,
            size.width,
            size.height
          );

          if (column === 0) {
            // Draw dice showing the sum
            const diceValues = this.getDiceValues(this.dices, number);
            page.drawDicesH(
              GUtil.basePen,
              rect,
              this.dices,
              PdfUnit.FromMillimeters(1),
              ...diceValues
            );
          } else {
            // Draw exercise cell
            const gen = currentGens[row * this.columns + column - 1];
            page.drawRectangle(GUtil.basePen, null, rect);
            page.drawStringsMulti(
              fixGenArray(gen.texts),
              font,
              PdfBrush.Black,
              null,
              rect,
              ETextAlign.Center
            );
          }
        }
      }
    }
  }

  /** Calculate dice values that sum to the target score */
  private getDiceValues(n: number, score: number): number[] {
    if (score < n || score > 6 * n) return [];

    const dice: number[] = [];

    // Start with all 1s
    for (let i = 0; i < n; i++) {
      dice[i] = 1;
    }

    let remaining = score - n;

    // Distribute the remaining points
    for (let i = 0; i < n && remaining > 0; i++) {
      const add = Math.min(5, remaining); // max we can add without exceeding 6
      dice[i] += add;
      remaining -= add;
    }

    return dice;
  }
}

// Variants with 1, 2, and 3 dice

export class HodKostkouVybarvi__1 extends RollDiceFillBase {
  constructor() {
    super(1);
  }
}

export class HodKostkouVybarvi__2 extends RollDiceFillBase {
  constructor() {
    super(2);
  }
}

export class HodKostkouVybarvi__3 extends RollDiceFillBase {
  constructor() {
    super(3);
  }
}
